import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Get Firebase app instance
const app = getApp();
const auth = getAuth(app);

// Function URL for createCheckoutSession
function getFunctionUrl(): string {
  const projectId = 'bizmarketplace-f6aaf';
  const region = 'us-central1';
  const functionName = 'createCheckoutSession';
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

// Hardcoded publishable key (sandbox)
const STRIPE_PUBLISHABLE_KEY = 'pk_test_51StX00CrTUcHtrFqlJyvXMwt0wiNwSNJAbDxjoj9k0Z9Wd3IntekHP44tgbU46mEMGMC1af8LAX3mgnkKAWMnXdY00pTlmAV80';

// Lazy-load Stripe - only create promise when needed
let stripePromise: Promise<Stripe | null> | null = null;

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY).catch((error) => {
      console.error('Failed to load Stripe:', error);
      return null;
    });
  }
  return stripePromise;
}

export interface PackageDetails {
  id: string;
  name: string;
  priceCAD: number;
  priceUSD: number;
  currency: string;
  description: string;
  features: string[];
}

// Package configurations (canonical IDs only)
export const PACKAGES: Record<string, PackageDetails> = {
  '3months': {
    id: '3months',
    name: '3-Months',
    priceCAD: 199,
    priceUSD: 149,
    currency: 'CAD',
    description: '3-month listing with basic features',
    features: [
      '3-month listing duration',
      'Instant lead notifications',
      'Expert email support',
      'REALTOR.ca listing included'
    ]
  },
  '6months': {
    id: '6months',
    name: '6-Months',
    priceCAD: 299,
    priceUSD: 249,
    currency: 'CAD',
    description: '6-month listing with premium features',
    features: [
      '6-month listing duration',
      'Instant lead notifications',
      'Expert email support',
      'REALTOR.ca listing included',
      'Featured listing placement'
    ]
  },
  '12months': {
    id: '12months',
    name: '12-Months',
    priceCAD: 399,
    priceUSD: 349,
    currency: 'CAD',
    description: 'Listing until sold with maximum exposure',
    features: [
      'Listing until sold',
      'Instant lead notifications',
      'Expert email support',
      'REALTOR.ca listing until sold',
      'Featured listing placement',
      'Email blast to subscribers'
    ]
  }
};

export class StripeService {
  private static instance: StripeService;
  private stripe: Stripe | null = null;
  private stripeInitializing: Promise<Stripe | null> | null = null;

  private constructor() {
    // Don't initialize eagerly - wait until actually needed
  }

  public static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  private async initializeStripe(): Promise<Stripe | null> {
    // If already initialized, return it
    if (this.stripe) {
      return this.stripe;
    }

    // If currently initializing, wait for that promise
    if (this.stripeInitializing) {
      this.stripe = await this.stripeInitializing;
      return this.stripe;
    }

    // Start initialization
    this.stripeInitializing = getStripePromise();
    try {
      this.stripe = await this.stripeInitializing;
      return this.stripe;
    } catch (error) {
      console.error('Failed to initialize Stripe:', error);
      this.stripeInitializing = null;
      return null;
    }
  }

  async createCheckoutSession(
    packageId: string,
    businessId: string,
    userId: string,
    currency: 'CAD' | 'USD',
    successUrl: string,
    cancelUrl: string,
    customerEmail?: string
  ) {
    try {
      const packageDetails = PACKAGES[packageId];
      if (!packageDetails) {
        throw new Error(`Invalid package ID: ${packageId}`);
      }

      // Get Firebase ID token if user is logged in
      let authHeader = '';
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const idToken = await currentUser.getIdToken();
          authHeader = `Bearer ${idToken}`;
        } catch (tokenError) {
          console.warn('Failed to get ID token, proceeding without auth header:', tokenError);
        }
      }

      // Call the function using fetch
      const functionUrl = getFunctionUrl();
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader && { 'Authorization': authHeader })
        },
        body: JSON.stringify({
          packageId,
          businessId,
          userId,
          currency,
          successUrl,
          cancelUrl,
          customerEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const { sessionId } = result;
      
      if (!sessionId) {
        throw new Error('No sessionId returned from server');
      }

      return sessionId;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  async redirectToCheckout(sessionId: string) {
    try {
      const stripe = await this.initializeStripe();
      
      if (!stripe) {
        throw new Error('Stripe is not available. Please check your Stripe configuration.');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  async processPayment(
    packageId: string,
    businessId: string,
    userId: string,
    currency: 'CAD' | 'USD',
    successUrl: string,
    cancelUrl: string,
    customerEmail?: string
  ) {
    try {
      const sessionId = await this.createCheckoutSession(
        packageId,
        businessId,
        userId,
        currency,
        successUrl,
        cancelUrl,
        customerEmail
      );
      
      await this.redirectToCheckout(sessionId);
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  getPackageDetails(packageId: string): PackageDetails | null {
    return PACKAGES[packageId] || null;
  }

  getAllPackages(): PackageDetails[] {
    return Object.values(PACKAGES);
  }

  /**
   * Verify payment status for a business listing
   * This is a fallback if the webhook hasn't fired yet
   */
  async verifyPayment(businessId: string): Promise<{ success: boolean; message: string; [key: string]: any }> {
    try {
      const app = getApp();
      const functions = getFunctions(app, 'us-central1');
      const verifyPaymentFn = httpsCallable(functions, 'verifyPayment');
      
      const result = await verifyPaymentFn({ businessId });
      return result.data as { success: boolean; message: string; [key: string]: any };
    } catch (error: any) {
      console.error('Error verifying payment:', error);
      throw new Error(error.message || 'Failed to verify payment');
    }
  }
}

export const stripeService = StripeService.getInstance();
export default stripeService; 